import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest } from '../../api/client';
import '../admin/quotes/quote-generator.css';

type InspectionRow = {
    _id: string;
    siteAddress?: string;
    status: 'in_progress' | 'completed';
    clientId?: { firstName?: string; lastName?: string; buildingName?: string } | null;
    technicianId?: { name?: string; email?: string } | null;
    createdAt?: string;
};

function clientName(i: InspectionRow): string {
    const c = i.clientId;
    if (!c) return '—';
    const n = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return n || c.buildingName || '—';
}

/**
 * Admins do not perform the on-site checklist. This page explains handoff to the assigned technician
 * and offers view of results only after the inspection is completed (summary).
 */
export default function AdminInspectionFieldNotice(): React.JSX.Element {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [row, setRow] = useState<InspectionRow | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!id) return;
        setError(null);
        apiRequest<{ data: InspectionRow }>(`/api/inspections/${id}`)
            .then((res) => {
                setRow(res.data);
                if (res.data?.status === 'completed') {
                    navigate(`/admin/inspections/${id}/summary`, { replace: true });
                }
            })
            .catch((e) => {
                setError(e instanceof Error ? e.message : 'Failed to load inspection.');
            });
    }, [id, navigate]);

    useEffect(() => {
        load();
    }, [load]);

    if (error) {
        return (
            <AppShell>
                <div className="quote-page">
                    <div className="quote-container" style={{ maxWidth: '640px' }}>
                        <div className="banner banner-error">{error}</div>
                        <button type="button" className="dash-btn dash-btn-ghost" onClick={() => navigate('/admin/inspections')}>
                            ← Back to inspections
                        </button>
                    </div>
                </div>
            </AppShell>
        );
    }

    if (!row) {
        return (
            <AppShell>
                <div className="quote-page">
                    <div className="quote-container" style={{ maxWidth: '640px' }}>
                        <p style={{ color: 'var(--dash-muted)' }}>Loading…</p>
                    </div>
                </div>
            </AppShell>
        );
    }

    const tech = row.technicianId?.name || row.technicianId?.email || 'Assigned technician';

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '640px' }}>
                    <header style={{ marginBottom: '24px' }}>
                        <h1 style={{ fontSize: '2rem', marginBottom: '8px' }}>Checklist in the field</h1>
                        <p style={{ color: 'var(--dash-muted)', margin: 0, lineHeight: 1.5 }}>
                            The on-site inspection checklist is completed by the <strong>assigned technician</strong> in the
                            technician portal, not in the admin console. When the inspection is finished, you can open the
                            full report and follow-ups from the completed inspection summary.
                        </p>
                    </header>

                    <div className="quote-card" style={{ padding: '20px 24px', marginBottom: '20px' }}>
                        <div className="quote-section-title" style={{ marginBottom: '12px' }}>This job</div>
                        <p style={{ margin: '0 0 6px' }}>
                            <span style={{ color: 'var(--dash-muted)' }}>Client: </span>
                            <strong>{clientName(row)}</strong>
                        </p>
                        <p style={{ margin: '0 0 6px' }}>
                            <span style={{ color: 'var(--dash-muted)' }}>Site: </span>
                            <strong>{row.siteAddress || '—'}</strong>
                        </p>
                        <p style={{ margin: '0 0 6px' }}>
                            <span style={{ color: 'var(--dash-muted)' }}>Technician: </span>
                            <strong>{tech}</strong>
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: 'var(--dash-muted)' }}>
                            Status:{' '}
                            <strong style={{ color: '#e6a817' }}>In progress</strong>
                            {row.createdAt ? (
                                <>
                                    {' '}
                                    · Scheduled/created {new Date(row.createdAt).toLocaleString()}
                                </>
                            ) : null}
                        </p>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        <button type="button" className="btn-primary" onClick={() => navigate('/admin/inspections')}>
                            View all inspections
                        </button>
                        <button type="button" className="dash-btn dash-btn-ghost" onClick={() => navigate('/admin/work-orders')}>
                            Back to jobs
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
